import { useState, useEffect } from "react";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Card, CardContent } from "../../components/ui/card";
import { Badge } from "../../components/ui/badge";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../../components/ui/table";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "../../components/ui/sheet";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../../components/ui/dialog";
import { Separator } from "../../components/ui/separator";
import { toast } from "sonner";
import {
  Plus, Eye, GitBranch, Zap, Globe, CheckCircle2,
  Clock, AlertTriangle, RefreshCw, Tag, Users, X, ArrowUpCircle,
  MessageSquareText, Layers, Lock, Ban,
} from "lucide-react";

const CATEGORIES = ["MARKETING", "UTILITY", "AUTHENTICATION"];
const EVENTS = ["renewal_reminder", "attendance_alert", "payment_due", "welcome", "expiry_notice", "custom"];
const HEADER_TYPES = ["TEXT", "IMAGE", "DOCUMENT", "VIDEO", "NONE"];

const EMPTY_CONTENT = { headerType: "TEXT", headerText: "", body: "", footer: "", buttons: [] };
const EMPTY_FORM = { name: "", category: "MARKETING", language: "en", automationEvent: "", notes: "", content: { ...EMPTY_CONTENT }, variables: "" };

function statusBadge(status) {
  if (status === "published")     return "bg-emerald-500/15 text-emerald-500 border-emerald-500/20";
  if (status === "draft")         return "bg-amber-500/15 text-amber-500 border-amber-500/20";
  if (status === "discontinued")  return "bg-rose-500/15 text-rose-500 border-rose-500/20";
  return "bg-zinc-500/15 text-zinc-400 border-zinc-500/20";
}

function metaBadge(metaStatus) {
  if (metaStatus === "APPROVED") return "bg-emerald-500/15 text-emerald-500 border-emerald-500/20";
  if (metaStatus === "REJECTED") return "bg-red-500/15 text-red-500 border-red-500/20";
  if (metaStatus === "PENDING")  return "bg-blue-500/15 text-blue-400 border-blue-500/20";
  return "";
}

export default function WhatsappTemplates() {
  const [templates, setTemplates]         = useState([]);
  const [loading, setLoading]             = useState(true);
  const [filterCat, setFilterCat]         = useState("");

  // detail drawer
  const [detailOpen, setDetailOpen]       = useState(false);
  const [detail, setDetail]               = useState(null);
  const [detailLoading, setDetailLoading] = useState(false);

  // create dialog
  const [createOpen, setCreateOpen]       = useState(false);
  const [createForm, setCreateForm]       = useState({ ...EMPTY_FORM });
  const [createLoading, setCreateLoading] = useState(false);

  // new-version dialog
  const [nvOpen, setNvOpen]               = useState(false);
  const [nvTemplate, setNvTemplate]       = useState(null);
  const [nvForm, setNvForm]               = useState({ content: { ...EMPTY_CONTENT }, variables: "", notes: "" });
  const [nvLoading, setNvLoading]         = useState(false);

  // publish dialog
  const [pubOpen, setPubOpen]             = useState(false);
  const [pubTemplate, setPubTemplate]     = useState(null);
  const [pubForm, setPubForm]             = useState({ metaTemplateId: "", metaTemplateName: "", metaStatus: "PENDING" });
  const [pubLoading, setPubLoading]       = useState(false);

  // discontinue state
  const [discLoading, setDiscLoading]     = useState(false);

  useEffect(() => { fetchTemplates(); }, [filterCat]);

  const fetchTemplates = async () => {
    setLoading(true);
    try {
      const res = await api.get(`/admin/whatsapp-templates${filterCat ? `?category=${filterCat}` : ""}`);
      setTemplates(res.data.templates);
    } catch { toast.error("Failed to load templates"); }
    finally { setLoading(false); }
  };

  const openDetail = async (templateId) => {
    setDetailLoading(true);
    setDetailOpen(true);
    try {
      const res = await api.get(`/admin/whatsapp-templates/${templateId}`);
      setDetail(res.data);
    } catch { toast.error("Failed to load template details"); }
    finally { setDetailLoading(false); }
  };

  // ── Create ──
  const handleCreate = async () => {
    if (!createForm.name.trim() || !createForm.content.body.trim()) {
      toast.error("Name and message body are required");
      return;
    }
    setCreateLoading(true);
    try {
      await api.post("/admin/whatsapp-templates", {
        ...createForm,
        variables: createForm.variables.split(",").map(s => s.trim()).filter(Boolean),
      });
      toast.success("Template created as draft");
      setCreateOpen(false);
      setCreateForm({ ...EMPTY_FORM });
      fetchTemplates();
    } catch (e) { toast.error(e?.response?.data?.detail || "Create failed"); }
    finally { setCreateLoading(false); }
  };

  // ── Publish ──
  const handlePublish = async () => {
    setPubLoading(true);
    try {
      await api.post(`/admin/whatsapp-templates/${pubTemplate.templateId}/publish`, pubForm);
      toast.success("Published!");
      setPubOpen(false);
      fetchTemplates();
      if (detail?.template?.templateId === pubTemplate.templateId) openDetail(pubTemplate.templateId);
    } catch (e) { toast.error(e?.response?.data?.detail || "Publish failed"); }
    finally { setPubLoading(false); }
  };

  // ── Discontinue ──
  const handleDiscontinue = async (templateId) => {
    if (!window.confirm("Mark this template as discontinued? Orgs currently using it will not be affected until they re-assign.")) return;
    setDiscLoading(true);
    try {
      await api.patch(`/admin/whatsapp-templates/${templateId}/discontinue`);
      toast.success("Template marked as discontinued");
      fetchTemplates();
      if (detail?.template?.templateId === templateId) openDetail(templateId);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to discontinue"); }
    finally { setDiscLoading(false); }
  };

  // ── New Version ──
  const handleNewVersion = async () => {
    if (!nvForm.content.body.trim()) { toast.error("Message body required"); return; }
    setNvLoading(true);
    try {
      await api.post(`/admin/whatsapp-templates/${nvTemplate.templateId}/new-version`, {
        content: nvForm.content,
        variables: nvForm.variables.split(",").map(s => s.trim()).filter(Boolean),
        notes: nvForm.notes,
      });
      toast.success("New draft version created");
      setNvOpen(false);
      fetchTemplates();
      if (detail?.template?.templateId === nvTemplate.templateId) openDetail(nvTemplate.templateId);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to create version"); }
    finally { setNvLoading(false); }
  };

  const openNewVersion = (t) => {
    setNvTemplate(t);
    const latest = t.versions?.slice(-1)[0];
    setNvForm({
      content: latest?.content ? { ...latest.content } : { ...EMPTY_CONTENT },
      variables: (latest?.variables || []).join(", "),
      notes: "",
    });
    setNvOpen(true);
  };

  const hasDraft         = (t) => t.versions?.some(v => v.status === "draft");
  const draftVersion     = (t) => t.versions?.find(v => v.status === "draft");
  const isDiscontinued   = (t) => t.isDiscontinued === true;
  const totalOrgs        = templates.reduce((s, t) => s + (t.orgCount || 0), 0);

  return (
    <div className="space-y-6">

      {/* ── Header ── */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-black font-heading tracking-tight uppercase">WhatsApp Templates</h1>
          <p className="text-sm text-muted-foreground mt-1">Master template library with version control</p>
        </div>
        <Button size="sm" onClick={() => setCreateOpen(true)} className="font-heading uppercase tracking-wide text-xs">
          <Plus className="h-4 w-4 mr-2" />New Template
        </Button>
      </div>

      {/* ── Stats ── */}
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        {[
          { label: "TEMPLATES",     value: templates.length,                                              icon: MessageSquareText, color: "" },
          { label: "PUBLISHED",     value: templates.filter(t => t.latestPublishedVersion).length,         icon: CheckCircle2,     color: "text-emerald-500" },
          { label: "DRAFT",         value: templates.filter(t => hasDraft(t)).length,                     icon: Clock,            color: "text-amber-500" },
          { label: "DISCONTINUED",  value: templates.filter(t => isDiscontinued(t)).length,               icon: Ban,              color: "text-rose-500" },
          { label: "ORG USAGES",    value: totalOrgs,                                                     icon: Users,            color: "text-blue-400" },
        ].map((s, i) => (
          <Card key={i} className="bg-card border-border">
            <CardContent className="p-3 sm:p-4">
              <s.icon className={`h-4 w-4 ${s.color || "text-muted-foreground"}`} />
              <p className={`text-2xl font-black font-heading mt-1.5 ${s.color}`}>{s.value}</p>
              <p className="text-[10px] text-muted-foreground font-mono uppercase tracking-widest">{s.label}</p>
            </CardContent>
          </Card>
        ))}
      </div>

      {/* ── Filter ── */}
      <div className="flex items-center gap-2">
        <Select value={filterCat || "all"} onValueChange={v => setFilterCat(v === "all" ? "" : v)}>
          <SelectTrigger className="w-44"><SelectValue placeholder="All Categories" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Categories</SelectItem>
            {CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
          </SelectContent>
        </Select>
        {filterCat && (
          <Button variant="ghost" size="sm" onClick={() => setFilterCat("")}>
            <X className="h-3.5 w-3.5 mr-1" />Clear
          </Button>
        )}
      </div>

      {/* ── Template List ── */}
      <Card className="bg-card border-border overflow-hidden">
        <Table>
          <TableHeader className="bg-secondary/30">
            <TableRow className="hover:bg-transparent">
              <TableHead>Template Name</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Event</TableHead>
              <TableHead className="text-center">Versions</TableHead>
              <TableHead>Latest Published</TableHead>
              <TableHead className="text-center">Orgs</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-12">
                  <div className="flex items-center justify-center gap-2">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent" />
                    <span className="text-muted-foreground text-sm">Loading...</span>
                  </div>
                </TableCell>
              </TableRow>
            ) : templates.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center py-16">
                  <div className="flex flex-col items-center gap-3">
                    <MessageSquareText className="h-10 w-10 text-muted-foreground/30" />
                    <p className="text-muted-foreground text-sm">No templates yet.</p>
                    <Button size="sm" onClick={() => setCreateOpen(true)}><Plus className="h-4 w-4 mr-1" />Create First Template</Button>
                  </div>
                </TableCell>
              </TableRow>
            ) : templates.map((t) => {
              const draft      = draftVersion(t);
              const pub        = t.latestPublishedVersion;
              const discontinued = isDiscontinued(t);
              return (
                <TableRow key={t.templateId} className={`group ${discontinued ? "opacity-60" : ""}`}>
                  <TableCell>
                    <div className="flex items-center gap-2">
                      <MessageSquareText className={`h-4 w-4 shrink-0 ${discontinued ? "text-rose-500" : "text-primary"}`} />
                      <div>
                        <p className="font-semibold text-sm font-mono">{t.name}</p>
                        <p className="text-[10px] text-muted-foreground font-mono">{t.language}</p>
                      </div>
                      {discontinued && (
                        <Badge className="bg-rose-500/15 text-rose-500 border border-rose-500/20 text-[9px] font-mono uppercase ml-1">Discontinued</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className="text-[10px] font-mono uppercase">{t.category}</Badge>
                  </TableCell>
                  <TableCell>
                    {t.automationEvent
                      ? <span className="inline-flex items-center gap-1 text-xs text-muted-foreground font-mono"><Zap className="h-3 w-3 text-amber-400" />{t.automationEvent}</span>
                      : <span className="text-muted-foreground text-xs">—</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    <div className="flex items-center justify-center gap-1">
                      <Layers className="h-3.5 w-3.5 text-muted-foreground" />
                      <span className="font-mono text-sm">{t.versionCount}</span>
                      {draft && (
                        <Badge className={`${statusBadge("draft")} border text-[9px] font-mono uppercase ml-1`}>Draft</Badge>
                      )}
                    </div>
                  </TableCell>
                  <TableCell>
                    {pub ? (
                      <div className="flex items-center gap-2">
                        <Badge className={`${statusBadge("published")} border text-[10px] font-mono uppercase`}>{pub.version}</Badge>
                        {pub.metaStatus && (
                          <Badge className={`${metaBadge(pub.metaStatus)} border text-[9px] font-mono uppercase`}>{pub.metaStatus}</Badge>
                        )}
                      </div>
                    ) : <span className="text-muted-foreground text-xs">Not published</span>}
                  </TableCell>
                  <TableCell className="text-center">
                    <span className="inline-flex items-center gap-1 font-mono text-sm">
                      <Globe className="h-3.5 w-3.5 text-muted-foreground" />{t.orgCount || 0}
                    </span>
                  </TableCell>
                  <TableCell className="text-right">
                    <div className="flex items-center justify-end gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                      {draft && !discontinued && (
                        <Button variant="outline" size="sm" className="h-7 text-[11px] font-heading uppercase text-emerald-500 border-emerald-500/30"
                          onClick={() => { setPubTemplate(t); setPubForm({ metaTemplateId: "", metaTemplateName: draft.metaTemplateName || "", metaStatus: "PENDING" }); setPubOpen(true); }}>
                          <CheckCircle2 className="h-3 w-3 mr-1" />Publish
                        </Button>
                      )}
                      {pub && !draft && !discontinued && (
                        <Button variant="outline" size="sm" className="h-7 text-[11px] font-heading uppercase"
                          onClick={() => openNewVersion(t)}>
                          <GitBranch className="h-3 w-3 mr-1" />New Version
                        </Button>
                      )}
                      {!discontinued && (
                        <Button variant="ghost" size="sm" className="h-7 text-[11px] font-heading uppercase text-rose-500 hover:text-rose-400"
                          onClick={() => handleDiscontinue(t.templateId)}>
                          <Ban className="h-3 w-3 mr-1" />Discontinue
                        </Button>
                      )}
                      <Button variant="ghost" size="sm" className="h-7 text-[11px] font-heading uppercase"
                        onClick={() => openDetail(t.templateId)}>
                        <Eye className="h-3 w-3 mr-1" />Details
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </Card>

      {/* ── Detail Drawer ── */}
      <Sheet open={detailOpen} onOpenChange={setDetailOpen}>
        <SheetContent side="right" className="w-full sm:max-w-2xl flex flex-col p-0 bg-card border-border overflow-y-auto">
          <SheetHeader className="px-6 pt-6 pb-4 border-b border-border shrink-0">
            <div className="flex items-center justify-between">
              <SheetTitle className="font-heading uppercase tracking-wide flex items-center gap-2">
                <MessageSquareText className="h-4 w-4 text-primary" />
                {detail?.template?.name || "Template Details"}
                {detail?.template?.isDiscontinued && (
                  <Badge className="bg-rose-500/15 text-rose-500 border border-rose-500/20 text-[9px] font-mono uppercase">Discontinued</Badge>
                )}
              </SheetTitle>
              <Button variant="ghost" size="icon" className="h-7 w-7" onClick={() => setDetailOpen(false)}>
                <X className="h-4 w-4" />
              </Button>
            </div>
          </SheetHeader>

          {detailLoading ? (
            <div className="flex items-center justify-center flex-1 py-20">
              <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
            </div>
          ) : detail ? (
            <div className="px-6 py-5 space-y-6">

              {/* meta */}
              <div className="flex flex-wrap gap-2">
                <Badge variant="outline" className="font-mono text-xs uppercase">{detail.template.category}</Badge>
                <Badge variant="outline" className="font-mono text-xs">{detail.template.language}</Badge>
                {detail.template.automationEvent && (
                  <Badge className="bg-amber-500/15 text-amber-500 border border-amber-500/20 text-xs font-mono">
                    <Zap className="h-3 w-3 mr-1" />{detail.template.automationEvent}
                  </Badge>
                )}
                {detail.template.isDiscontinued && (
                  <Badge className="bg-rose-500/15 text-rose-500 border border-rose-500/20 text-xs font-mono">
                    <Ban className="h-3 w-3 mr-1" />Discontinued
                  </Badge>
                )}
              </div>

              {/* versions timeline */}
              <div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3">Version History</p>
                <div className="space-y-3">
                  {(detail.versions || []).map((v) => (
                    <div key={v.templateVersionId} className="rounded-xl border border-border bg-secondary/30 p-4 space-y-3">
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2">
                          <Badge className={`${statusBadge(v.status)} border text-[10px] font-mono uppercase`}>{v.version}</Badge>
                          <Badge className={`${statusBadge(v.status)} border text-[10px] font-mono uppercase`}>{v.status}</Badge>
                          {v.status === "published" && <Lock className="h-3 w-3 text-muted-foreground" />}
                          {v.status === "discontinued" && <Ban className="h-3 w-3 text-rose-500" />}
                        </div>
                        {v.metaStatus && (
                          <Badge className={`${metaBadge(v.metaStatus)} border text-[9px] font-mono uppercase`}>
                            Meta: {v.metaStatus}
                          </Badge>
                        )}
                      </div>

                      {/* header preview */}
                      {v.content?.headerType && v.content.headerType !== "NONE" && (
                        <div className="flex items-center gap-2">
                          <span className="text-[10px] font-mono text-muted-foreground uppercase">Header:</span>
                          <Badge variant="outline" className="text-[10px] font-mono">{v.content.headerType}</Badge>
                          {v.content.headerText && <span className="text-xs text-muted-foreground">{v.content.headerText}</span>}
                        </div>
                      )}

                      {v.content?.body && (
                        <div className="bg-background/60 rounded-lg p-3 text-xs font-mono whitespace-pre-wrap text-muted-foreground border border-border">
                          {v.content.body}
                        </div>
                      )}

                      {v.variables?.length > 0 && (
                        <div className="flex flex-wrap gap-1">
                          {v.variables.map(vr => (
                            <span key={vr} className="inline-flex items-center gap-0.5 bg-primary/10 text-primary text-[10px] font-mono px-1.5 py-0.5 rounded">
                              <Tag className="h-2.5 w-2.5" />{vr}
                            </span>
                          ))}
                        </div>
                      )}

                      {v.metaTemplateName && (
                        <p className="text-[10px] font-mono text-muted-foreground">Meta name: {v.metaTemplateName}</p>
                      )}
                      {v.notes && <p className="text-xs text-muted-foreground italic">{v.notes}</p>}
                      <p className="text-[10px] font-mono text-muted-foreground">
                        Created: {v.createdAt?.slice(0, 10)}
                        {v.publishedAt ? ` · Published: ${v.publishedAt.slice(0, 10)}` : ""}
                      </p>
                    </div>
                  ))}
                </div>
              </div>

              <Separator />

              {/* org mappings */}
              <div>
                <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground mb-3">Organization Mappings ({detail.orgMappings?.length || 0})</p>
                {detail.orgMappings?.length === 0 ? (
                  <p className="text-sm text-muted-foreground">No organizations using this template yet.</p>
                ) : (
                  <div className="space-y-2">
                    {detail.orgMappings.map((m) => (
                      <div key={m.orgTemplateId} className="flex items-center justify-between gap-2 rounded-lg border border-border bg-secondary/20 px-3 py-2">
                        <div>
                          <p className="text-sm font-medium">{m.organizationName}</p>
                          <p className="text-[10px] font-mono text-muted-foreground">
                            On version: {m.versionTag} · {m.isEnabled ? "Enabled" : "Disabled"}
                          </p>
                        </div>
                        <div className="flex items-center gap-2">
                          {m.hasUpgrade && (
                            <Badge className="bg-blue-500/15 text-blue-400 border border-blue-500/20 text-[10px] font-mono animate-pulse">
                              <ArrowUpCircle className="h-3 w-3 mr-0.5" />Upgrade available
                            </Badge>
                          )}
                          {!m.isEnabled && (
                            <Badge className="bg-zinc-500/15 text-zinc-400 border-zinc-500/20 text-[10px] font-mono">Disabled</Badge>
                          )}
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* actions */}
              {!detail.template?.isDiscontinued && (
                <div className="flex flex-wrap gap-2 pt-2">
                  {detail.versions?.some(v => v.status === "draft") && (
                    <Button size="sm" className="text-xs font-heading uppercase text-emerald-500 bg-emerald-500/10 hover:bg-emerald-500/20"
                      onClick={() => {
                        setPubTemplate(detail.template);
                        const d = detail.versions.find(v => v.status === "draft");
                        setPubForm({ metaTemplateId: "", metaTemplateName: d?.metaTemplateName || "", metaStatus: "PENDING" });
                        setPubOpen(true);
                      }}>
                      <CheckCircle2 className="h-3.5 w-3.5 mr-1" />Publish Draft
                    </Button>
                  )}
                  {detail.versions?.some(v => v.status === "published") && !detail.versions?.some(v => v.status === "draft") && (
                    <Button variant="outline" size="sm" className="text-xs font-heading uppercase"
                      onClick={() => openNewVersion(detail.template)}>
                      <GitBranch className="h-3.5 w-3.5 mr-1" />New Version
                    </Button>
                  )}
                  <Button variant="outline" size="sm" className="text-xs font-heading uppercase text-rose-500 border-rose-500/30 hover:bg-rose-500/10"
                    disabled={discLoading}
                    onClick={() => handleDiscontinue(detail.template.templateId)}>
                    <Ban className="h-3.5 w-3.5 mr-1" />Discontinue Template
                  </Button>
                </div>
              )}
            </div>
          ) : null}
        </SheetContent>
      </Sheet>

      {/* ── Create Dialog ── */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="bg-card border-border max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-wide">New WhatsApp Template</DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">Creates as draft. Publish after Meta approval.</DialogDescription>
          </DialogHeader>

          <div className="space-y-5 mt-2">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Template Name *</Label>
                <Input placeholder="e.g. renewal_reminder" value={createForm.name}
                  onChange={e => setCreateForm(f => ({ ...f, name: e.target.value.toLowerCase().replace(/\s+/g, "_") }))}
                  className="font-mono" />
                <p className="text-[10px] text-muted-foreground font-mono">snake_case · used as Meta template name</p>
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Category *</Label>
                <Select value={createForm.category} onValueChange={v => setCreateForm(f => ({ ...f, category: v }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Language</Label>
                <Input value={createForm.language} onChange={e => setCreateForm(f => ({ ...f, language: e.target.value }))} placeholder="en" />
              </div>
              <div className="space-y-1.5">
                <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Automation Event</Label>
                <Select value={createForm.automationEvent || "none"} onValueChange={v => setCreateForm(f => ({ ...f, automationEvent: v === "none" ? "" : v }))}>
                  <SelectTrigger><SelectValue placeholder="None" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">None</SelectItem>
                    {EVENTS.map(e => <SelectItem key={e} value={e}>{e}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Separator />
            <p className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Message Content</p>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Header Type</Label>
                <Select value={createForm.content.headerType} onValueChange={v => setCreateForm(f => ({ ...f, content: { ...f.content, headerType: v } }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{HEADER_TYPES.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {createForm.content.headerType === "TEXT" && (
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Header Text</Label>
                  <Input value={createForm.content.headerText}
                    onChange={e => setCreateForm(f => ({ ...f, content: { ...f.content, headerText: e.target.value } }))}
                    placeholder="e.g. Renewal Reminder" />
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Message Body *</Label>
              <Textarea rows={5} value={createForm.content.body}
                onChange={e => setCreateForm(f => ({ ...f, content: { ...f.content, body: e.target.value } }))}
                placeholder={"Hi {{1}}, your membership expires on {{2}}. Renew now!"} className="font-mono text-sm" />
              <p className="text-[10px] text-muted-foreground font-mono">{"Use {{1}}, {{2}} etc. for dynamic variables"}</p>
            </div>

            <div className="space-y-1.5">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Footer</Label>
              <Input value={createForm.content.footer}
                onChange={e => setCreateForm(f => ({ ...f, content: { ...f.content, footer: e.target.value } }))}
                placeholder="Reply STOP to unsubscribe" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Variables (comma-separated)</Label>
              <Input value={createForm.variables}
                onChange={e => setCreateForm(f => ({ ...f, variables: e.target.value }))}
                placeholder="member_name, expiry_date" className="font-mono" />
            </div>

            <div className="space-y-1.5">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Notes</Label>
              <Textarea rows={2} value={createForm.notes}
                onChange={e => setCreateForm(f => ({ ...f, notes: e.target.value }))}
                placeholder="Internal notes about this template..." />
            </div>
          </div>

          <div className="flex gap-2 pt-4">
            <Button className="flex-1 font-heading uppercase tracking-wide" onClick={handleCreate} disabled={createLoading}>
              {createLoading ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-foreground border-t-transparent mr-2" /> : <Plus className="h-4 w-4 mr-2" />}
              Create Draft
            </Button>
            <Button variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── Publish Dialog ── */}
      <Dialog open={pubOpen} onOpenChange={setPubOpen}>
        <DialogContent className="bg-card border-border max-w-md">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-wide flex items-center gap-2">
              <CheckCircle2 className="h-4 w-4 text-emerald-500" />Publish Version
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Published versions are <strong>immutable</strong>. Fill in Meta details after WhatsApp approval.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="space-y-1.5">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Meta Template Name</Label>
              <Input value={pubForm.metaTemplateName} onChange={e => setPubForm(f => ({ ...f, metaTemplateName: e.target.value }))} className="font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Meta Template ID</Label>
              <Input value={pubForm.metaTemplateId} onChange={e => setPubForm(f => ({ ...f, metaTemplateId: e.target.value }))} className="font-mono" placeholder="From Meta Business Manager" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Meta Approval Status</Label>
              <Select value={pubForm.metaStatus} onValueChange={v => setPubForm(f => ({ ...f, metaStatus: v }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="PENDING">PENDING</SelectItem>
                  <SelectItem value="APPROVED">APPROVED</SelectItem>
                  <SelectItem value="REJECTED">REJECTED</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="rounded-lg border border-amber-500/20 bg-amber-500/5 p-3 flex items-start gap-2">
              <AlertTriangle className="h-4 w-4 text-amber-500 shrink-0 mt-0.5" />
              <p className="text-xs text-amber-500">Once published, this version cannot be edited. Create a new version to make changes.</p>
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button className="flex-1 font-heading uppercase" onClick={handlePublish} disabled={pubLoading}>
              {pubLoading ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-foreground border-t-transparent mr-2" /> : <CheckCircle2 className="h-4 w-4 mr-2" />}
              Confirm Publish
            </Button>
            <Button variant="ghost" onClick={() => setPubOpen(false)}>Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* ── New Version Dialog ── */}
      <Dialog open={nvOpen} onOpenChange={setNvOpen}>
        <DialogContent className="bg-card border-border max-w-2xl w-full max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-heading uppercase tracking-wide flex items-center gap-2">
              <GitBranch className="h-4 w-4 text-primary" />New Version — {nvTemplate?.name}
            </DialogTitle>
            <DialogDescription className="text-xs text-muted-foreground">
              Creates a new draft. Existing org users stay on their current version.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-2">
            <div className="rounded-lg border border-blue-500/20 bg-blue-500/5 p-3 flex items-start gap-2">
              <RefreshCw className="h-4 w-4 text-blue-400 shrink-0 mt-0.5" />
              <p className="text-xs text-blue-400">A new Meta template will be created (e.g. <code className="font-mono">{nvTemplate?.name}_v{(nvTemplate?.versionCount || 0) + 1}</code>) since WhatsApp does not allow editing approved templates.</p>
            </div>

            {/* Header Type + Text */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Header Type</Label>
                <Select value={nvForm.content.headerType} onValueChange={v => setNvForm(f => ({ ...f, content: { ...f.content, headerType: v, headerText: v !== "TEXT" ? "" : f.content.headerText } }))}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>{HEADER_TYPES.map(h => <SelectItem key={h} value={h}>{h}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              {nvForm.content.headerType === "TEXT" && (
                <div className="space-y-1.5">
                  <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Header Text</Label>
                  <Input value={nvForm.content.headerText}
                    onChange={e => setNvForm(f => ({ ...f, content: { ...f.content, headerText: e.target.value } }))}
                    placeholder="e.g. Renewal Reminder" />
                </div>
              )}
            </div>

            <div className="space-y-1.5">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Message Body *</Label>
              <Textarea rows={6} value={nvForm.content.body}
                onChange={e => setNvForm(f => ({ ...f, content: { ...f.content, body: e.target.value } }))}
                className="font-mono text-sm" />
              <p className="text-[10px] text-muted-foreground font-mono">{"Use {{1}}, {{2}} etc. for dynamic variables"}</p>
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Variables (comma-separated)</Label>
              <Input value={nvForm.variables} onChange={e => setNvForm(f => ({ ...f, variables: e.target.value }))} className="font-mono" />
            </div>
            <div className="space-y-1.5">
              <Label className="text-[10px] font-mono uppercase tracking-widest text-muted-foreground">Change Notes</Label>
              <Textarea rows={2} value={nvForm.notes} onChange={e => setNvForm(f => ({ ...f, notes: e.target.value }))} placeholder="What changed in this version..." />
            </div>
          </div>
          <div className="flex gap-2 pt-2">
            <Button className="flex-1 font-heading uppercase" onClick={handleNewVersion} disabled={nvLoading}>
              {nvLoading ? <div className="animate-spin rounded-full h-4 w-4 border-2 border-primary-foreground border-t-transparent mr-2" /> : <GitBranch className="h-4 w-4 mr-2" />}
              Create Draft Version
            </Button>
            <Button variant="ghost" onClick={() => setNvOpen(false)}>Cancel</Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
